import type { AlertProps } from '@mui/material'
import { Snackbar } from '@mui/material'
import moment from 'moment-timezone'
import type { ComponentType, ReactElement, ReactNode } from 'react'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { unique } from 'remeda'
import { isOnPremConnectorOfflineError } from 'siteline-common-all/utils/integration'

const DEFAULT_DURATION = 6000
const DEFAULT_ERROR_DURATION = 12000

type SnackbarContextType = {
  show: (
    message: string,
    severity: AlertProps['color'],
    autoClose: boolean,
    duration?: number
  ) => void
  /** Updates the message of the currently visible snackbar without re-triggering the animation */
  updateMessage: (message: string) => void
  showLoading: (message?: string, autoClose?: boolean) => void
  showWarning: (message: string, duration?: number) => void
  showError: (error: string, duration?: number) => void
  showErrors: (errors: string[], duration?: number) => void
  recordError: (error: Error, message: string, duration?: number) => void
  showSuccess: (message?: string | ReactElement) => void
  showInfo: (message: string) => void
  showInfos: (message: string[]) => void
  closeAll: () => void
}

const SnackbarContext = createContext<SnackbarContextType>({
  show: (): void => undefined,
  updateMessage: (): void => undefined,
  showLoading: (): void => undefined,
  showWarning: (): void => undefined,
  showError: (): void => undefined,
  showErrors: (): void => undefined,
  recordError: (): void => undefined,
  showSuccess: (): void => undefined,
  showInfo: (): void => undefined,
  showInfos: (): void => undefined,
  closeAll: (): void => undefined,
})

export function useSitelineSnackbar() {
  return useContext(SnackbarContext)
}

type SitelineSnackbarProviderProps = {
  children: ReactNode
  AlertComponent: ComponentType<AlertProps>
}

// Inspired from https://github.com/TeamWertarbyte/material-ui-snackbar-provider
export function SitelineSnackbarProvider({
  children,
  AlertComponent,
}: SitelineSnackbarProviderProps) {
  const [message, setMessage] = useState<string | ReactElement>('')
  const [open, setOpen] = useState(false)
  const [severity, setSeverity] = useState<AlertProps['color']>('success')
  const [autoClose, setAutoClose] = useState(false)
  const [key, setKey] = useState<string | undefined>()
  const [autoHideDuration, setAutoHideDuration] = useState<number | undefined>()

  const handleShow = useCallback(
    (
      newMessage: string | ReactElement,
      newSeverity: AlertProps['color'],
      newAutoClose: boolean,
      duration?: number
    ) => {
      setMessage(newMessage)
      setSeverity(newSeverity)
      setAutoClose(newAutoClose)
      setOpen(true)
      setKey(moment.utc().valueOf().toString())
      setAutoHideDuration(duration)
    },
    []
  )

  const handleUpdateMessage = useCallback((newMessage: string) => {
    setMessage(newMessage)
  }, [])

  const handleCloseAll = useCallback(() => {
    setOpen(false)
  }, [])

  const handleShowLoading = useCallback(
    (loadingMessage?: string, shouldAutoClose = false) => {
      handleShow(loadingMessage ?? 'Performing action...', 'info', shouldAutoClose)
    },
    [handleShow]
  )

  const handleShowWarning = useCallback(
    (warningMessage: string, duration = DEFAULT_DURATION) => {
      handleShow(warningMessage, 'warning', true, duration)
    },
    [handleShow]
  )

  const handleShowError = useCallback(
    (error: string, duration = DEFAULT_ERROR_DURATION) => {
      // We intercept the Agave Connector error, since it will already be shown by the
      // `AgaveConnectorOfflinePopup` (based on an Apollo link intercepting GraphQL errors
      // in `SitelineApolloProvider`), so we don't need to show it here as well. Simply close any
      // visible snackbars and return.
      if (isOnPremConnectorOfflineError(error)) {
        handleCloseAll()
        return
      }
      handleShow(error, 'error', true, duration)
    },
    [handleCloseAll, handleShow]
  )

  const handleShowErrors = useCallback(
    (errors: string[], duration = DEFAULT_ERROR_DURATION) => {
      const uniqueErrors = unique(errors)
      const error = (
        <>
          {uniqueErrors.map((error) => (
            <p key={error}>{error}</p>
          ))}
        </>
      )
      handleShow(error, 'error', true, duration)
    },
    [handleShow]
  )

  const handleRecordError = useCallback(
    (error: Error, errorMessage: string, duration = DEFAULT_ERROR_DURATION) => {
      console.error(error)
      handleShow(errorMessage, 'error', true, duration)
    },
    [handleShow]
  )

  const handleShowSuccess = useCallback(
    (successMessage?: string | ReactElement) => {
      handleShow(successMessage ?? 'Done!', 'success', true)
    },
    [handleShow]
  )

  const handleShowInfo = useCallback(
    (infoMessage: string) => {
      handleShow(infoMessage, 'info', true)
    },
    [handleShow]
  )

  const handleShowInfos = useCallback(
    (messages: string[]) => {
      const uniqueMessages = unique(messages)
      const infoContent = (
        <>
          {uniqueMessages.map((msg) => (
            <p key={msg}>{msg}</p>
          ))}
        </>
      )
      handleShow(infoContent, 'info', true)
    },
    [handleShow]
  )

  const contextValue = useMemo(
    () => ({
      show: handleShow,
      updateMessage: handleUpdateMessage,
      showLoading: handleShowLoading,
      showWarning: handleShowWarning,
      showError: handleShowError,
      showErrors: handleShowErrors,
      recordError: handleRecordError,
      showSuccess: handleShowSuccess,
      showInfo: handleShowInfo,
      showInfos: handleShowInfos,
      closeAll: handleCloseAll,
    }),
    [
      handleCloseAll,
      handleRecordError,
      handleShow,
      handleUpdateMessage,
      handleShowError,
      handleShowErrors,
      handleShowInfo,
      handleShowInfos,
      handleShowLoading,
      handleShowSuccess,
      handleShowWarning,
    ]
  )

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const jsxMessage = <AlertComponent severity={severity}>{message}</AlertComponent>

  return (
    <>
      <SnackbarContext.Provider value={contextValue}>{children}</SnackbarContext.Provider>
      <SitelineSnackbar
        snackbarKey={key}
        jsxMessage={jsxMessage}
        open={open}
        autoClose={autoClose}
        onClose={handleClose}
        closeOnClickAway={autoClose}
        autoHideDuration={autoHideDuration}
      />
    </>
  )
}

interface SitelineSnackbarProps {
  open: boolean
  onClose: () => void
  jsxMessage: ReactElement
  autoClose: boolean
  closeOnClickAway: boolean
  snackbarKey?: string
  autoHideDuration?: number
}

export function SitelineSnackbar({
  open,
  onClose,
  jsxMessage,
  autoClose,
  closeOnClickAway,
  snackbarKey,
  autoHideDuration = DEFAULT_DURATION,
}: SitelineSnackbarProps) {
  const handleClose = useCallback(
    (_event: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway' && !closeOnClickAway) {
        return
      }
      onClose()
    },
    [closeOnClickAway, onClose]
  )

  return (
    <Snackbar
      key={snackbarKey}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={open}
      autoHideDuration={autoClose ? autoHideDuration : null}
      onClose={handleClose}
      sx={{ maxWidth: 700 }}
    >
      {jsxMessage}
    </Snackbar>
  )
}
